import { cn } from "../../../lib/utils";
import {
  COMPOSITION_PARTS,
  compositionOverhead,
  formatBytes,
  formatCount,
  formatPercent,
  type RunConfig,
  type Trace,
  type TraceRound,
  type TraceTarget,
} from "./trace";

const PART_CLASS_NAMES = {
  system: "bg-ds-slate-400",
  tools: "bg-ds-slate-300",
  user: "bg-ds-pine-400",
  assistant: "bg-ds-blackberry-400",
  reasoning: "bg-ds-pitaya-300",
  toolCalls: "bg-ds-lemon-400",
  toolResults: "bg-ds-clementine-400",
} as const satisfies Record<(typeof COMPOSITION_PARTS)[number], string>;

function usage(round: TraceRound): { input: number; cached: number | null } {
  const outcome = round.row.outcome;
  if (outcome === null) return { input: 0, cached: null };
  switch (outcome.kind) {
    case "completed":
      return { input: outcome.inputTokens, cached: outcome.cachedInputTokens };
    case "failed":
      return { input: 0, cached: null };
    default:
      throw new Error(outcome satisfies never);
  }
}

// How each agent's prompt grows round after round, against the context window.
export function ContextTab({
  config,
  trace,
  onSelect,
}: {
  config: RunConfig | null;
  trace: Trace;
  onSelect: (target: TraceTarget) => void;
}): React.ReactElement {
  const rounds = trace.agents.flatMap((agent) => agent.rounds);
  // The window bounds the bars; before the config loads, the largest prompt does.
  const scale =
    config === null
      ? Math.max(1, ...rounds.map((round) => round.row.promptBytes))
      : config.limits.contextWindow;
  const maxInput = Math.max(1, ...rounds.map((round) => usage(round).input));
  return (
    <div className="flex flex-col gap-4 p-3 font-mono text-xs text-ds-black">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-ds-slate-500">
        {COMPOSITION_PARTS.map((part) => (
          <span key={part} className="flex items-center gap-1">
            <span className={cn("size-2 rounded-sm", PART_CLASS_NAMES[part])} />
            {part}
          </span>
        ))}
        <span>
          {config === null
            ? "bars scale to the largest prompt"
            : `bars scale to the window, ${formatBytes(config.limits.contextWindow)}`}
        </span>
      </div>
      {trace.agents
        .filter((agent) => agent.rounds.length > 0)
        .map((agent) => (
          <section key={agent.key} className="flex flex-col gap-1">
            <h3 className="m-0 font-dm-sans text-sm font-semibold">
              {agent.label}
            </h3>
            <div
              className="flex h-16 items-end gap-px border-b border-ds-slate-200"
              aria-label="Input tokens per round, cached part darker"
            >
              {agent.rounds.map((round) => {
                const { input, cached } = usage(round);
                return (
                  <button
                    key={round.row.id}
                    type="button"
                    title={`round ${String(round.index)}: in ${formatCount(input)}, cached ${cached === null ? "n/a" : formatCount(cached)}`}
                    className="relative flex min-w-1 flex-1 flex-col justify-end bg-ds-moss-200 hover:bg-ds-moss-300"
                    style={{ height: `${String((input / maxInput) * 100)}%` }}
                    onClick={() => {
                      onSelect({ kind: "round", id: round.row.id });
                    }}
                  >
                    {cached !== null && input > 0 && (
                      <span
                        className="block w-full bg-ds-moss-600"
                        style={{
                          height: `${String((Math.min(cached, input) / input) * 100)}%`,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
            {agent.rounds.map((round) => {
              const { input, cached } = usage(round);
              const overhead = compositionOverhead(round.row);
              return (
                <button
                  key={round.row.id}
                  type="button"
                  className="flex w-full items-center gap-2 py-0.5 text-left hover:bg-ds-slate-100"
                  onClick={() => {
                    onSelect({ kind: "round", id: round.row.id });
                  }}
                >
                  <span className="w-16 shrink-0">{`round ${String(round.index)}`}</span>
                  <span className="relative flex h-3 min-w-0 flex-1 overflow-hidden rounded-sm bg-ds-slate-100">
                    {COMPOSITION_PARTS.map((part) => (
                      <span
                        key={part}
                        title={`${part}: ${formatBytes(round.row.composition[part])}`}
                        className={cn(
                          "h-full shrink-0",
                          PART_CLASS_NAMES[part]
                        )}
                        style={{
                          width: `${String((round.row.composition[part] / scale) * 100)}%`,
                        }}
                      />
                    ))}
                    {overhead > 0 && (
                      <span
                        title={`JSON overhead: ${formatBytes(overhead)}`}
                        className="h-full shrink-0 bg-ds-slate-200"
                        style={{
                          width: `${String((overhead / scale) * 100)}%`,
                        }}
                      />
                    )}
                  </span>
                  <span className="w-28 shrink-0 text-right tabular-nums">
                    {config === null
                      ? formatBytes(round.row.promptBytes)
                      : `${formatBytes(round.row.promptBytes)} ${formatPercent(round.row.promptBytes / config.limits.contextWindow)}`}
                  </span>
                  <span className="w-28 shrink-0 text-right text-ds-slate-500 tabular-nums">
                    {`in ${formatCount(input)}${cached === null || input === 0 ? "" : ` ${formatPercent(cached / input)}`}`}
                  </span>
                </button>
              );
            })}
          </section>
        ))}
    </div>
  );
}
